import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import brush from '../../sources/Graph/Brush_purple.png';

export default function ServiceFlow() {
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState(0);

    const tabs = [
        t('ServiceFlow.card1_title'),
        t('ServiceFlow.card2_title'),
        t('ServiceFlow.card3_title'),
        t('ServiceFlow.card4_title')
    ];

    const textStyle = {
        fontFamily: "Noto Sans TC",
        color: "#4E433C",
        fontSize: { xs: 16, md: 18 },
        fontWeight: 400,
        lineHeight: "180%",
        textAlign: "justify",
        letterSpacing: "0.5px"
    };

    const listItemStyle = {
        ...textStyle,
        marginBottom: '12px'
    };

    return (
        <Box width="100%" sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'transparent' }}>
            {/* Title Section */}
            <Typography
                fontFamily={"Noto Sans TC"}
                textAlign={"center"}
                fontSize={{ xs: "28px", md: "42px" }}
                fontWeight={700}
                color='#7DC4A2'
                mb={6}
                sx={{
                    textShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    lineHeight: '100%',
                    position: 'relative',
                    backgroundImage: `url(${brush})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundPositionY: { xs: 25, md: 45 }
                }}
            >
                {t('ServiceFlow.title')}
            </Typography>

            <Box sx={{ maxWidth: '1200px', margin: '0 auto', px: { xs: 2, md: 4 } }}>
                {/* Custom Elegant Tabs / Pills */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexWrap: 'nowrap', 
                        justifyContent: { xs: 'flex-start', md: 'center' }, 
                        overflowX: 'auto',
                        gap: { xs: 1.5, md: 3 }, 
                        mb: { xs: 5, md: 8 },
                        py: 2, // Sufficient padding top & bottom for hover translateY and box-shadow
                        '::-webkit-scrollbar': { height: '0px' }, // Hide scrollbar for a cleaner look
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none'
                    }}
                >
                    {tabs.map((tabLabel, index) => (
                        <Box
                            key={index}
                            onClick={() => setActiveTab(index)}
                            sx={{
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                padding: { xs: '8px 18px', md: '12px 32px' },
                                borderRadius: '40px',
                                border: '2px solid',
                                borderColor: activeTab === index ? '#7DC4A2' : 'rgba(125, 196, 162, 0.3)',
                                backgroundColor: activeTab === index ? '#7DC4A2' : 'transparent',
                                color: activeTab === index ? '#fff' : '#7DC4A2',
                                fontWeight: activeTab === index ? 700 : 500,
                                fontSize: { xs: '14px', md: '18px' },
                                fontFamily: 'Noto Sans TC',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: activeTab === index ? '0px 8px 20px rgba(125, 196, 162, 0.3)' : 'none',
                                '&:hover': {
                                    backgroundColor: activeTab === index ? '#7DC4A2' : 'rgba(125, 196, 162, 0.08)',
                                    borderColor: '#7DC4A2',
                                    transform: 'translateY(-4px)'
                                }
                            }}
                        >
                            {tabLabel}
                        </Box>
                    ))}
                </Box>

                {/* Content Section (Diary Style: Text only, Grid stacking for uniform height) */}
                <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', mt: { xs: 0, md: 2 }, px: { xs: 1, md: 4 }, maxWidth: '850px', mx: 'auto' }}>
                    
                    {/* Slide 1 */}
                    <Box sx={{ 
                        gridArea: '1 / 1', 
                        opacity: activeTab === 0 ? 1 : 0, 
                        visibility: activeTab === 0 ? 'visible' : 'hidden', 
                        pointerEvents: activeTab === 0 ? 'auto' : 'none',
                        transition: 'opacity 0.6s ease, visibility 0.6s',
                        zIndex: activeTab === 0 ? 1 : 0 
                    }}>
                        <Box sx={{ px: { xs: 1, md: 2 } }}>
                            <Typography sx={textStyle}>{t('ServiceFlow.card1_text')}</Typography>
                        </Box>
                    </Box>

                    {/* Slide 2 */}
                    <Box sx={{ 
                        gridArea: '1 / 1', 
                        opacity: activeTab === 1 ? 1 : 0, 
                        visibility: activeTab === 1 ? 'visible' : 'hidden', 
                        pointerEvents: activeTab === 1 ? 'auto' : 'none',
                        transition: 'opacity 0.6s ease, visibility 0.6s',
                        zIndex: activeTab === 1 ? 1 : 0 
                    }}>
                        <Box sx={{ px: { xs: 1, md: 2 } }}>
                            <Typography sx={{ ...textStyle, mb: 2 }}>{t('ServiceFlow.card2_text1')}</Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0, paddingLeft: '20px' }}>
                                <Box component="li" sx={listItemStyle}><strong>{t('ServiceFlow.card2_list1_title')}</strong> {t('ServiceFlow.card2_list1_text')}</Box>
                                <Box component="li" sx={listItemStyle}><strong>{t('ServiceFlow.card2_list2_title')}</strong> {t('ServiceFlow.card2_list2_text')}</Box>
                                <Box component="li" sx={listItemStyle}><strong>{t('ServiceFlow.card2_list3_title')}</strong> {t('ServiceFlow.card2_list3_text')}</Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Slide 3 */}
                    <Box sx={{ 
                        gridArea: '1 / 1', 
                        opacity: activeTab === 2 ? 1 : 0, 
                        visibility: activeTab === 2 ? 'visible' : 'hidden', 
                        pointerEvents: activeTab === 2 ? 'auto' : 'none',
                        transition: 'opacity 0.6s ease, visibility 0.6s',
                        zIndex: activeTab === 2 ? 1 : 0 
                    }}>
                        <Box sx={{ px: { xs: 1, md: 2 } }}>
                            <Typography sx={{ ...textStyle, mb: 2 }}>{t('ServiceFlow.card3_text1')}</Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0, mb: 2, paddingLeft: '20px' }}>
                                <Box component="li" sx={listItemStyle}><strong>{t('ServiceFlow.card3_list1_title')}</strong> {t('ServiceFlow.card3_list1_text')}</Box>
                                <Box component="li" sx={listItemStyle}><strong>{t('ServiceFlow.card3_list2_title')}</strong> {t('ServiceFlow.card3_list2_text')}</Box>
                            </Box>
                            <Typography sx={textStyle}>{t('ServiceFlow.card3_text2')}</Typography>
                        </Box>
                    </Box>

                    {/* Slide 4 */}
                    <Box sx={{ 
                        gridArea: '1 / 1', 
                        opacity: activeTab === 3 ? 1 : 0, 
                        visibility: activeTab === 3 ? 'visible' : 'hidden', 
                        pointerEvents: activeTab === 3 ? 'auto' : 'none',
                        transition: 'opacity 0.6s ease, visibility 0.6s',
                        zIndex: activeTab === 3 ? 1 : 0 
                    }}>
                        <Box sx={{ px: { xs: 1, md: 2 } }}>
                            <Typography sx={{ ...textStyle, mb: 2 }}>{t('ServiceFlow.card4_text1')}</Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0, paddingLeft: '20px' }}>
                                <Box component="li" sx={listItemStyle}>{t('ServiceFlow.card4_list1')}</Box>
                                <Box component="li" sx={listItemStyle}>{t('ServiceFlow.card4_list2')}</Box>
                                <Box component="li" sx={listItemStyle}>{t('ServiceFlow.card4_list3')}</Box>
                                <Box component="li" sx={listItemStyle}>{t('ServiceFlow.card4_list4')}</Box>
                                <Box component="li" sx={listItemStyle}>{t('ServiceFlow.card4_list5')}</Box>
                            </Box>
                        </Box>
                    </Box>

                </Box>
            </Box>
        </Box>
    );
}
